import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Code, CheckCircle2, Clock, AlertCircle, FileText, Search, Filter, X, Calendar, User, Tag, ChevronLeft, ChevronRight, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { DatePicker } from '../components/DatePicker';
import { toast } from 'sonner';
import { format } from 'date-fns';
import TaskDetailModal from '../components/TaskDetailModal';
import { getBrandGradientClasses } from '../utils/brandColors';
import { sortTasksByTabStatus, getOrderingLabel } from '../utils/taskOrdering';
import { queueTaskEditHandoff } from '../utils/taskEditHandoff';
import { useBrands } from '../utils/brands';
import { useTaskOptions, getOptionColor, optionBadgeStyle, canonicalKeyForName, nameForCanonicalKey } from '../utils/taskOptions';
import { matchesTaskSearch } from '../utils/taskSearch';
import { MultiSelectFilter } from '../components/MultiSelectFilter';
import { ActiveFilterChips } from '../components/ActiveFilterChips';
import { TaskIdBadge } from '../components/TaskIdBadge';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TASKS_PER_PAGE_OPTIONS = [10, 25, 50];

const DeveloperBoard = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [taskNotes, setTaskNotes] = useState({});
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUser, setFilterUser] = useState([]);
  const [filterCategory, setFilterCategory] = useState([]);
  const [filterBrand, setFilterBrand] = useState([]);
  const [filterOwnerStatus, setFilterOwnerStatus] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [devCompletedDate, setDevCompletedDate] = useState('');

  // Brand master list (same source as the Task Table) — keeps the dropdown in
  // lockstep with the rest of the app and auto-refreshes when admins edit it.
  const BRANDS = useBrands();
  const { CATEGORIES: TASK_CATEGORIES, DEVELOPER_STATUSES, TASK_STATUSES, RAW: TASK_OPTIONS_RAW } = useTaskOptions();
  const devStatusKey = (name) => canonicalKeyForName(TASK_OPTIONS_RAW.developer_statuses, name);
  // Working (non-terminal) developer statuses drive the status-change dropdown;
  // "Completed" is reached via approval, not directly selectable here.
  const DEV_WORKING_STATUSES = DEVELOPER_STATUSES.filter(s => devStatusKey(s) !== 'completed');
  const YET_TO_START_NAME = nameForCanonicalKey(TASK_OPTIONS_RAW.developer_statuses, 'yet_to_start', DEV_WORKING_STATUSES[0] || 'Yet to Start');
  
  // Active tab state for developer status
  const [activeDevStatusTab, setActiveDevStatusTab] = useState('Yet to Start');
  
  // Pagination states - separate for each tab
  const [paginationState, setPaginationState] = useState({
    'Yet to Start': { currentPage: 1, tasksPerPage: 10 },
    'In Progress': { currentPage: 1, tasksPerPage: 10 },
    'Pending Approval': { currentPage: 1, tasksPerPage: 10 },
    'Completed': { currentPage: 1, tasksPerPage: 10 }
  });
  
  // Use centralized permission from AuthContext
  const { isReadOnly } = useAuth();

  useEffect(() => {
    loadDeveloperTasks();
    loadUsers();
  }, []);

  const loadDeveloperTasks = async () => {
    try {
      const response = await axios.get(`${API}/tasks?developer_dependency=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(response.data);
      
      // Initialize notes state with existing notes from tasks
      const notesMap = {};
      response.data.forEach(task => {
        notesMap[task.id] = task.notes || '';
      });
      setTaskNotes(notesMap);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const updateDeveloperStatus = async (taskId, newStatus) => {
    try {
      await axios.put(
        `${API}/tasks/${taskId}`,
        { 
          developer_status: newStatus,
          // Clear any rejection notes when developer moves the task forward.
          developer_approval_status: null,
          developer_manager_notes: null,
          notes: taskNotes[taskId] || ''
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Status updated!');
      loadDeveloperTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const submitForApproval = async (taskId) => {
    try {
      await axios.post(
        `${API}/tasks/${taskId}/developer-submit-approval`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Submitted for manager approval');
      loadDeveloperTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit for approval');
    }
  };

  const handleNotesChange = (taskId, value) => {
    setTaskNotes(prev => ({
      ...prev,
      [taskId]: value
    }));
  };

  const saveNotes = async (taskId) => {
    try {
      await axios.put(
        `${API}/tasks/${taskId}`,
        { notes: taskNotes[taskId] || '', note_source: 'developer_note' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Notes saved!');
      loadDeveloperTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save notes');
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'Completed') return <CheckCircle2 className="text-green-600" size={20} />;
    if (status === 'In Progress') return <Clock className="text-blue-600" size={20} />;
    if (status === 'Pending Approval') return <AlertCircle className="text-amber-600" size={20} />;
    if (status === 'Yet to Start') return <AlertCircle className="text-orange-600" size={20} />;
    return <AlertCircle className="text-orange-600" size={20} />;
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? user.full_name : 'Unassigned';
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setFilterUser([]);
    setFilterCategory([]);
    setFilterBrand([]);
    setFilterOwnerStatus([]);
    setFromDate('');
    setToDate('');
    setDevCompletedDate('');
    toast.success('All filters cleared');
  };

  const hasActiveFilters = searchTerm || filterUser.length > 0 || filterCategory.length > 0 || filterBrand.length > 0 || filterOwnerStatus.length > 0 || fromDate || toDate || devCompletedDate;

  // Reset pagination to page 1 when filters change
  useEffect(() => {
    setPaginationState(prev => ({
      'Yet to Start': { ...prev['Yet to Start'], currentPage: 1 },
      'In Progress': { ...prev['In Progress'], currentPage: 1 },
      'Pending Approval': { ...prev['Pending Approval'], currentPage: 1 },
      'Completed': { ...prev['Completed'], currentPage: 1 }
    }));
  }, [searchTerm, filterUser, filterCategory, filterBrand, filterOwnerStatus, fromDate, toDate, devCompletedDate]);

  // Get current pagination state for active tab
  const currentPagination = paginationState[activeDevStatusTab];
  const { currentPage, tasksPerPage } = currentPagination;

  // Handle page change
  const handlePageChange = (newPage) => {
    setPaginationState(prev => ({
      ...prev,
      [activeDevStatusTab]: { ...prev[activeDevStatusTab], currentPage: newPage }
    }));
  };

  // Handle tasks per page change
  const handleTasksPerPageChange = (value) => {
    const newTasksPerPage = parseInt(value);
    setPaginationState(prev => ({
      ...prev,
      [activeDevStatusTab]: { tasksPerPage: newTasksPerPage, currentPage: 1 }
    }));
  };

  // Handle tab change - reset to page 1 for that tab
  const handleTabChange = (status) => {
    setActiveDevStatusTab(status);
    // Reset to page 1 when switching tabs
    setPaginationState(prev => ({
      ...prev,
      [status]: { ...prev[status], currentPage: 1 }
    }));
  };

  // Apply all filters
  const filteredTasks = tasks.filter(task => {
    // Search filter
    if (searchTerm && !matchesTaskSearch(task, searchTerm)) {
      return false;
    }
    
    // User filter
    if (filterUser.length > 0 && !filterUser.includes(task.task_done_by)) {
      return false;
    }
    
    // Category filter
    if (filterCategory.length > 0 && !filterCategory.includes(task.category)) {
      return false;
    }

    // Brand filter — match exact brand string (Task Table uses the same comparison).
    if (filterBrand.length > 0 && !filterBrand.includes(task.brand)) {
      return false;
    }

    // Owner status filter
    if (filterOwnerStatus.length > 0 && !filterOwnerStatus.includes(task.your_status)) {
      return false;
    }
    
    // Date range filter
    if (fromDate || toDate) {
      const taskDate = task.start_date ? new Date(task.start_date) : null;
      if (!taskDate) return false;
      
      if (fromDate && taskDate < new Date(fromDate)) return false;
      if (toDate && taskDate > new Date(toDate)) return false;
    }
    
    // Developer Completed Date filter
    if (devCompletedDate) {
      // Task must have a developer_end_date to match this filter
      if (!task.developer_end_date) return false;
      
      const completedDate = new Date(task.developer_end_date);
      const filterDate = new Date(devCompletedDate);
      
      // Compare only the date portion (ignore time)
      const completedDateStr = completedDate.toISOString().split('T')[0];
      const filterDateStr = filterDate.toISOString().split('T')[0];
      
      if (completedDateStr !== filterDateStr) return false;
    }
    
    return true;
  });

  // Group tasks by developer_status — 4-tab layout: Yet to Start / In Progress / Pending Approval / Completed.
  // "In Progress" covers In Development → Assets Pending → Need to Live → Live Done.
  // Bucketing is canonical_key-based (not literal name matching) so an admin
  // rename of any Developer Status still routes tasks into the right tab.
  // Each tab is sorted with the unified workflow-priority strategy (see utils/taskOrdering.js)
  // so ordering stays consistent across pagination, filters, search and refreshes.
  const DEV_COMPLETED_FIELDS = ['developer_end_date', 'your_end_date', 'updated_at'];
  const groupedTasks = {
    'Yet to Start': sortTasksByTabStatus(
      filteredTasks.filter(t => !t.developer_status || devStatusKey(t.developer_status) === 'yet_to_start'),
      'Yet to Start'
    ),
    'In Progress': sortTasksByTabStatus(
      filteredTasks.filter(t => {
        if (!t.developer_status) return false;
        const key = devStatusKey(t.developer_status);
        return key !== 'yet_to_start' && key !== 'completed';
      }),
      'In Progress'
    ),
    'Pending Approval': sortTasksByTabStatus(
      filteredTasks.filter(t => t.developer_approval_status === 'Pending'),
      'Pending Approval'
    ),
    'Completed': sortTasksByTabStatus(
      filteredTasks.filter(t => devStatusKey(t.developer_status) === 'completed'),
      'Completed',
      { completedDateFields: DEV_COMPLETED_FIELDS }
    ),
  };

  // Pagination calculations for active tab
  const activeTabTasks = groupedTasks[activeDevStatusTab];
  const totalTasks = activeTabTasks.length;
  const totalPages = Math.ceil(totalTasks / tasksPerPage);
  const startIndex = (currentPage - 1) * tasksPerPage;
  const endIndex = startIndex + tasksPerPage;
  const paginatedTasks = activeTabTasks.slice(startIndex, endIndex);

  // Generate page numbers for pagination UI
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      // Always show last page
      if (!pages.includes(totalPages)) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="developer-board">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-2xl p-6 border border-cyan-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl text-white shadow-lg">
              <Code size={32} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Developer Queue</h1>
              <p className="text-slate-600 mt-1">Tasks requiring developer attention • {filteredTasks.length} total tasks</p>
            </div>
          </div>
          
          {/* Quick Stats */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-center px-3 py-2 bg-white rounded-lg border border-slate-200">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide">Yet to Start</p>
              <p className="text-xl font-bold text-orange-600">{groupedTasks['Yet to Start'].length}</p>
            </div>
            <div className="text-center px-3 py-2 bg-white rounded-lg border border-slate-200">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide">In Progress</p>
              <p className="text-xl font-bold text-blue-600">{groupedTasks['In Progress'].length}</p>
            </div>
            <div className="text-center px-3 py-2 bg-white rounded-lg border border-slate-200">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide">Pending</p>
              <p className="text-xl font-bold text-amber-600" data-testid="stat-pending-approval">{groupedTasks['Pending Approval'].length}</p>
            </div>
            <div className="text-center px-3 py-2 bg-white rounded-lg border border-slate-200">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide">Completed</p>
              <p className="text-xl font-bold text-green-600">{groupedTasks['Completed'].length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Filter className="text-cyan-600" size={20} />
          <h2 className="text-lg font-semibold text-slate-900">Filters</h2>
          {hasActiveFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearAllFilters}
              className="ml-auto text-slate-600 border-slate-300 hover:bg-slate-50"
            >
              <X size={14} className="mr-1" />
              Clear All Filters
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <Label className="text-xs text-slate-600 mb-2 flex items-center gap-1">
              <Search size={12} />
              Search Tasks
            </Label>
            <Input
              type="text"
              placeholder="Search by task name or Task ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
              data-testid="search-tasks"
            />
          </div>

          {/* User Filter */}
          <div>
            <Label className="text-xs text-slate-600 mb-2 flex items-center gap-1">
              <User size={12} />
              Assigned User
            </Label>
            <MultiSelectFilter
              label="Assigned User"
              placeholder="All Users"
              options={users.map(u => ({ value: u.id, label: u.full_name, searchText: u.email }))}
              selectedValues={filterUser}
              onChange={setFilterUser}
              testId="filter-user"
            />
          </div>

          {/* Category Filter */}
          <div>
            <Label className="text-xs text-slate-600 mb-2 flex items-center gap-1">
              <Tag size={12} />
              Category
            </Label>
            <MultiSelectFilter
              label="Category"
              placeholder="All Categories"
              options={TASK_CATEGORIES}
              selectedValues={filterCategory}
              onChange={setFilterCategory}
              testId="filter-category"
            />
          </div>

          {/* Brand Filter — same component + behaviour as Task Table */}
          <div>
            <Label className="text-xs text-slate-600 mb-2 flex items-center gap-1">
              <Award size={12} />
              Brand
            </Label>
            <MultiSelectFilter
              label="Brand"
              placeholder="All Brands"
              options={BRANDS}
              selectedValues={filterBrand}
              onChange={setFilterBrand}
              testId="filter-brand"
            />
          </div>

          {/* Owner Status Filter */}
          <div>
            <Label className="text-xs text-slate-600 mb-2">Owner Status</Label>
            <MultiSelectFilter
              label="Owner Status"
              placeholder="All Statuses"
              options={TASK_STATUSES}
              selectedValues={filterOwnerStatus}
              onChange={setFilterOwnerStatus}
              testId="filter-owner-status"
            />
          </div>

          {/* Date Range */}
          <div className="lg:col-span-2 grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs text-slate-600 mb-2 flex items-center gap-1">
                <Calendar size={12} />
                From Date
              </Label>
              <DatePicker
                value={fromDate}
                onChange={setFromDate}
                testId="filter-from-date"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600 mb-2">To Date</Label>
              <DatePicker
                value={toDate}
                onChange={setToDate}
                testId="filter-to-date"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600 mb-2 flex items-center gap-1">
                <CheckCircle2 size={12} />
                Dev Completed
              </Label>
              <DatePicker
                value={devCompletedDate}
                onChange={setDevCompletedDate}
                testId="filter-dev-completed-date"
              />
            </div>
          </div>
        </div>

        {/* Active Filters Indicator */}
        {hasActiveFilters && (
          <div className="mt-4 pt-4 border-t border-slate-200">
            <p className="text-sm text-slate-600">
              <span className="font-semibold">Showing:</span> {filteredTasks.length} of {tasks.length} tasks
            </p>
            <ActiveFilterChips
              chips={[
                filterUser.length > 0 && { key: 'user', label: `${filterUser.length} User${filterUser.length > 1 ? 's' : ''}`, onRemove: () => setFilterUser([]) },
                filterCategory.length > 0 && { key: 'category', label: `${filterCategory.length} Categor${filterCategory.length > 1 ? 'ies' : 'y'}`, onRemove: () => setFilterCategory([]) },
                filterBrand.length > 0 && { key: 'brand', label: `${filterBrand.length} Brand${filterBrand.length > 1 ? 's' : ''}`, onRemove: () => setFilterBrand([]) },
                filterOwnerStatus.length > 0 && { key: 'owner-status', label: `${filterOwnerStatus.length} Status${filterOwnerStatus.length > 1 ? 'es' : ''}`, onRemove: () => setFilterOwnerStatus([]) },
              ].filter(Boolean)}
              onClearAll={clearAllFilters}
            />
          </div>
        )}
      </div>

      {/* Developer Status Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex items-center gap-2" data-testid="developer-status-tabs">
          {['Yet to Start', 'In Progress', 'Pending Approval', 'Completed'].map((status) => {
            const count = groupedTasks[status].length;
            const isActive = activeDevStatusTab === status;
            
            return (
              <button
                key={status}
                onClick={() => handleTabChange(status)}
                data-testid={`dev-tab-${status.toLowerCase().replace(/\s+/g, '-')}`}
                className={`
                  flex items-center gap-3 px-5 py-3 rounded-full font-semibold transition-all duration-200
                  ${isActive 
                    ? 'bg-slate-900 text-white shadow-lg' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }
                `}
              >
                <span className="text-sm">{status}</span>
                <span className={`
                  text-lg font-bold
                  ${isActive ? 'text-white' : 'text-slate-800'}
                `}>
                  {count.toString().padStart(2, '0')}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Task List for Active Tab */}
      <div className="space-y-4">
        <div className="flex items-center justify-between pb-2 border-b-2 border-slate-200">
          <div className="flex items-center gap-3">
            {getStatusIcon(activeDevStatusTab)}
            <h2 className="text-2xl font-bold text-slate-800">{activeDevStatusTab}</h2>
            <span className="px-3 py-1 rounded-full text-sm font-semibold bg-slate-100 text-slate-700">
              {totalTasks} {totalTasks === 1 ? 'task' : 'tasks'}
            </span>
            <span
              data-testid="dev-queue-sort-indicator"
              className="hidden sm:inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-cyan-50 text-cyan-700 border border-cyan-200 font-medium"
              title="Default ordering applied to this tab"
            >
              Sorted: {getOrderingLabel(activeDevStatusTab)}
            </span>
          </div>
          {totalTasks > 0 && (
            <span className="text-sm text-slate-500">
              Showing {startIndex + 1}-{Math.min(endIndex, totalTasks)} of {totalTasks}
            </span>
          )}
        </div>
        
        {totalTasks === 0 ? (
          <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border-2 border-dashed border-slate-300 p-12 text-center">
            <AlertCircle className="text-slate-400 mx-auto mb-3" size={48} />
            <p className="text-slate-500 text-lg font-medium">No {activeDevStatusTab.toLowerCase()} tasks</p>
            <p className="text-slate-400 text-sm mt-1">
              {hasActiveFilters ? 'Try adjusting your filters' : 'All tasks are in other statuses'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {paginatedTasks.map((task) => (
              <div
                key={task.id}
                data-testid={`dev-task-${task.id}`}
                onClick={() => {
                  setSelectedTask(task);
                  setIsDetailOpen(true);
                }}
                className="group bg-white rounded-lg shadow-sm border border-slate-200 hover:border-cyan-400 p-3 hover:shadow-md transition-all cursor-pointer"
              >
                <div className="flex items-start gap-4">
                  {/* Left Content - Task Details */}
                  <div className="flex-1 min-w-0">
                    {/* Top Row: Badges + Title */}
                    <div className="flex items-start gap-3 mb-1.5">
                      <div className="flex items-center gap-1.5 flex-wrap flex-shrink-0">
                        <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded font-semibold bg-purple-100 text-purple-700">
                          <Tag size={10} />
                          {task.category}
                        </span>
                        {task.brand && (
                          <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded font-semibold ${getBrandGradientClasses(task.brand)}`}>
                            {task.brand}
                          </span>
                        )}
                        {task.your_status && (
                          <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${
                            task.your_status === 'Completed' ? 'bg-green-100 text-green-700' :
                            task.your_status === 'In Process' ? 'bg-blue-100 text-blue-700' :
                            task.your_status === 'On Hold' ? 'bg-red-100 text-red-700' :
                            task.your_status === 'Backlog' ? 'bg-slate-100 text-slate-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {task.your_status}
                          </span>
                        )}
                        {task.task_done_by && (
                          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded font-semibold bg-slate-100 text-slate-600">
                            <User size={10} />
                            {getUserName(task.task_done_by)}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Task Title */}
                    <h3 className="text-sm font-semibold text-slate-900 mb-1.5 group-hover:text-cyan-600 transition-colors line-clamp-2">
                      <TaskIdBadge taskId={task.task_id} className="mr-1.5 align-middle" />
                      {task.task}
                    </h3>
                    
                    {/* Task Output - Compact inline */}
                    {task.output && (
                      <div className="flex items-start gap-2 mb-1.5">
                        <span className="text-[10px] font-semibold text-cyan-600 uppercase whitespace-nowrap">Output:</span>
                        <p className="text-xs text-slate-600 line-clamp-1">{task.output}</p>
                      </div>
                    )}
                    
                    {/* Task Notes - Compact inline */}
                    {task.notes && (
                      <div className="flex items-start gap-2 mb-1.5">
                        <span className="text-[10px] font-semibold text-slate-500 uppercase whitespace-nowrap flex items-center gap-0.5">
                          <FileText size={10} />
                          Notes:
                        </span>
                        <p className="text-xs text-slate-600 line-clamp-1">{task.notes}</p>
                      </div>
                    )}
                    
                    {/* Dates - More compact */}
                    <div className="flex items-center gap-3 text-[10px] text-slate-400">
                      {task.start_date && (
                        <span className="flex items-center gap-1">
                          <Calendar size={10} />
                          {format(new Date(task.start_date), 'MMM dd, yyyy')}
                        </span>
                      )}
                      {task.developer_end_date && (
                        <span className="flex items-center gap-1 text-green-600 font-semibold">
                          <CheckCircle2 size={10} />
                          Done: {format(new Date(task.developer_end_date), 'MMM dd')}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Right Content - Actions (Compact Horizontal) */}
                  {!isReadOnly && (
                    <div className="flex items-start gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      {/* Status Selector */}
                      <div className="bg-slate-50 rounded p-1.5 border border-slate-200 min-w-[140px]">
                        <Label className="text-[9px] font-semibold text-slate-500 mb-0.5 uppercase block">
                          Dev Status
                        </Label>
                        {task.developer_approval_status === 'Pending' ? (
                          <div
                            data-testid={`dev-pending-approval-${task.id}`}
                            className="h-7 px-2 flex items-center justify-center text-[11px] font-semibold rounded bg-amber-100 text-amber-700 border border-amber-200"
                            title="Awaiting manager approval"
                          >
                            Pending Approval
                          </div>
                        ) : (
                          <Select
                            value={
                              DEV_WORKING_STATUSES.includes(task.developer_status)
                                ? task.developer_status
                                : (devStatusKey(task.developer_status) === 'completed' ? task.developer_status : YET_TO_START_NAME)
                            }
                            onValueChange={(value) => updateDeveloperStatus(task.id, value)}
                          >
                            <SelectTrigger className="bg-white border-slate-300 h-7 text-[11px]" data-testid={`dev-status-select-${task.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {DEV_WORKING_STATUSES.map((s) => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                              ))}
                              {devStatusKey(task.developer_status) === 'completed' && (
                                <SelectItem value={task.developer_status}>{task.developer_status}</SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                        )}
                        {/* Submit-for-Approval CTA — appears from Live Done */}
                        {devStatusKey(task.developer_status) === 'live_done' && task.developer_approval_status !== 'Pending' && (
                          <button
                            onClick={() => submitForApproval(task.id)}
                            data-testid={`submit-approval-${task.id}`}
                            className="mt-1 w-full text-[10px] font-semibold px-2 py-1 rounded bg-gradient-to-r from-green-500 to-emerald-500 hover:opacity-90 text-white shadow-sm"
                            title="Send to Manager for completion approval"
                          >
                            Submit for Approval
                          </button>
                        )}
                        {task.developer_approval_status === 'Rejected' && task.developer_manager_notes && (
                          <div
                            data-testid={`dev-rejection-notes-${task.id}`}
                            className="mt-1 text-[10px] text-red-700 bg-red-50 border border-red-100 rounded px-1.5 py-1 leading-tight"
                            title={task.developer_manager_notes}
                          >
                            <span className="font-semibold">Rejected:</span> {task.developer_manager_notes}
                          </div>
                        )}
                      </div>
                      
                      {/* Developer Notes - Compact Inline */}
                      <div className="bg-slate-50 rounded p-1.5 border border-slate-200 min-w-[180px]">
                        <Label className="text-[9px] font-semibold text-slate-500 mb-0.5 uppercase flex items-center gap-0.5">
                          <FileText size={9} />
                          Notes
                        </Label>
                        <div className="flex gap-1">
                          <Input
                            value={taskNotes[task.id] || ''}
                            onChange={(e) => handleNotesChange(task.id, e.target.value)}
                            placeholder="Add notes..."
                            className="h-7 text-[11px] flex-1"
                            data-testid={`dev-notes-${task.id}`}
                          />
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              saveNotes(task.id);
                            }}
                            className="h-7 px-2 text-[10px] bg-cyan-500 hover:bg-cyan-600"
                            data-testid={`save-notes-${task.id}`}
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination Controls */}
        {totalTasks > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mt-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              {/* Page Navigation */}
              <div className="flex items-center gap-2" data-testid="pagination-controls">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="flex items-center gap-1"
                  data-testid="pagination-prev"
                >
                  <ChevronLeft size={16} />
                  Previous
                </Button>
                
                <div className="flex items-center gap-1">
                  {getPageNumbers().map((page, index) => (
                    page === '...' ? (
                      <span key={`ellipsis-${index}`} className="px-2 text-slate-400">...</span>
                    ) : (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(page)}
                        className={`min-w-[40px] ${currentPage === page ? 'bg-slate-900 text-white' : ''}`}
                        data-testid={`pagination-page-${page}`}
                      >
                        {page}
                      </Button>
                    )
                  ))}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="flex items-center gap-1"
                  data-testid="pagination-next"
                >
                  Next
                  <ChevronRight size={16} />
                </Button>
              </div>
              
              {/* Tasks Per Page Selector */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">Tasks per page:</span>
                <Select value={tasksPerPage.toString()} onValueChange={handleTasksPerPageChange}>
                  <SelectTrigger className="w-[80px]" data-testid="tasks-per-page-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TASKS_PER_PAGE_OPTIONS.map(option => (
                      <SelectItem key={option} value={option.toString()}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Page Info */}
            <div className="mt-3 pt-3 border-t border-slate-200 text-center">
              <span className="text-sm text-slate-500">
                Page {currentPage} of {totalPages} • Showing {startIndex + 1}-{Math.min(endIndex, totalTasks)} of {totalTasks} tasks
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Task Detail Modal */}
      <TaskDetailModal
        task={selectedTask}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        users={users}
        onTaskUpdated={loadDeveloperTasks}
        onEdit={(task) => {
          queueTaskEditHandoff(task.id);
          setIsDetailOpen(false);
          navigate('/tasks');
        }}
      />
    </div>
  );
};

export default DeveloperBoard;
